/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

/* added by KoderWeb */
/**
 * Lightweight size picker for homepage / collection grids.
 *
 * Two variants of the same idea render together (radios for desktop,
 * a <select> for mobile — toggled purely with CSS, see the Liquid
 * snippet). Both call the same shared addVariantToCart()/showCartError()
 * helpers below so the cart logic only lives in one place.
 */

async function addVariantToCart(variantId) {
  document.dispatchEvent(new CustomEvent('theme:loading:start'));

  const cartAddUrl = (window.routes && window.routes.cartAddUrl) || '/cart/add';

  try {
    const response = await fetch(`${cartAddUrl}.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        // Required by Shopify's cart endpoint, per Prestige's own code
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    });

    document.dispatchEvent(new CustomEvent('theme:loading:end'));

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.description || 'Could not add this item to your cart.');
    }

    return data;
  } catch (error) {
    document.dispatchEvent(new CustomEvent('theme:loading:end'));
    throw error;
  }
}

function showCartError(message, element) {
  // Prestige's grid markup: .ProductItem wraps the whole card, and
  // .ProductItem__Info holds the title/price/sizes block. Insert the
  // error right above that block.
  const card = element.closest('.ProductItem');
  const infoElement = card ? card.querySelector('.ProductItem__Info') : null;

  const errorMessageElement = document.createElement('span');
  errorMessageElement.className = 'ProductForm__Error Alert Alert--error';
  errorMessageElement.textContent = message;

  if (infoElement) {
    infoElement.insertAdjacentElement('beforebegin', errorMessageElement);
  } else {
    element.insertAdjacentElement('afterend', errorMessageElement);
  }

  setTimeout(() => errorMessageElement.remove(), 2500);
}

function syncHiddenIdInput(sectionId, variantId) {
  const form = document.getElementById(sectionId);
  const idInput = form ? form.querySelector('input[name="id"]') : null;
  if (idInput) idInput.value = variantId;
}

/**
 * Desktop: tap-to-add size chips.
 */
class VariantRadios extends HTMLElement {
  constructor() {
    super();
    // 'click' instead of 'change': a radio only fires 'change' when its
    // checked state transitions, so clicking an already-selected size
    // (e.g. trying to add a 2nd unit of a 1-in-stock item) would never
    // fire anything with 'change'. 'click' fires every time.
    this.addEventListener('click', this.onSizeClick.bind(this));
  }

  onSizeClick(event) {
    // Filter to the radio input itself. Clicking a <label for="..."> also
    // dispatches a forwarded click on its input (target = input) in
    // addition to the original click on the label (target = label) — this
    // check ensures we only react once, to the input-targeted event.
    if (!event.target.matches('input[type="radio"]')) return;

    const variantId = event.target.dataset.variantId;
    if (!variantId) return; // disabled/sold-out inputs never fire click at all

    this.addToCart(variantId, event.target);
  }

  async addToCart(variantId, input) {
    input.classList.add('loading');

    try {
      const data = await addVariantToCart(variantId);

      syncHiddenIdInput(this.dataset.section, variantId);

      // Prestige's documented hook: the mini-cart listens for this on the
      // document and re-renders itself.
      this.dispatchEvent(
        new CustomEvent('product:added', {
          bubbles: true,
          detail: { variant: data, quantity: 1 },
        })
      );
    } catch (error) {
      showCartError(error.message, this);
      console.error('Add to cart failed:', error);
    } finally {
      input.classList.remove('loading');
    }
  }
}

customElements.define('variant-radios', VariantRadios);

/**
 * Mobile: a native <select> doing the same job.
 */
class VariantSelect extends HTMLElement {
  constructor() {
    super();
    this.select = this.querySelector('select');
    this.addEventListener('change', this.onSizeChange.bind(this));
  }

  onSizeChange(event) {
    const option = event.target.selectedOptions[0];
    const variantId = option ? option.dataset.variantId : null;
    if (!variantId) return; // the disabled placeholder option

    this.addToCart(variantId);
  }

  async addToCart(variantId) {
    this.select.classList.add('loading');

    try {
      const data = await addVariantToCart(variantId);

      syncHiddenIdInput(this.dataset.section, variantId);

      this.dispatchEvent(
        new CustomEvent('product:added', {
          bubbles: true,
          detail: { variant: data, quantity: 1 },
        })
      );
    } catch (error) {
      showCartError(error.message, this);
      console.error('Add to cart failed:', error);
    } finally {
      this.select.classList.remove('loading');
      // A native <select> only fires 'change' on an actual value
      // transition, same limitation as radios. Reset to the placeholder
      // so picking the SAME size again still fires a real 'change' event.
      this.select.selectedIndex = 0;
    }
  }
}

customElements.define('variant-select', VariantSelect);